import React, { useState, useEffect } from "react";
// api gére les requetes vers l'api avec axios et la variable d'environnement;
import api from "../../api";
import UseTitle from "../../components/useTitle";
import "./CharacterUpdate.scss";

const CharacterUpdate = ({ character, setIsUpdate, setRefresh }) => {
  // Définition du titre de la page
  UseTitle("Ajouter des métiers, compétences et sorts");

  const [isLoading, setIsLoading] = useState(false);
  const [messageError, setMessageError] = useState();

  // Récupération des jobs triés du personnage
  const jobsOwned = character.jobs.sort((a, b) => a.name.localeCompare(b.name));

  // Récupération des skills triés du personnage
  const skillsOwned = jobsOwned
    .flatMap((job) => job.skills)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Récupération des spells triés du personnage
  const spellsOwned = jobsOwned
    .flatMap((job) => job.spells)
    .sort((a, b) => a.name.localeCompare(b.name));

  // JOB variables
  const [jobsAdd, setJobsAdd] = useState([]);
  const [jobSearch, setJobSearch] = useState("");
  const [jobPageSize, setJobPageSize] = useState(6);
  const [jobPage, setJobPage] = useState(1);
  const [jobList, setJobList] = useState([]);
  const [jobListInfos, setJobListInfos] = useState([]);
  const [jobIsInitialLoading, setJobIsInitialLoading] = useState(true);
  const [jobIsRefreshing, setJobIsRefreshing] = useState(false);

  // SKILL variables
  const [skillsAdd, setSkillsAdd] = useState([]);
  const [skillSearch, setSkillSearch] = useState("");
  const [skillPageSize, setSkillPageSize] = useState(6);
  const [skillPage, setSkillPage] = useState(1);
  const [skillList, setSkillList] = useState([]);
  const [skillListInfos, setSkillListInfos] = useState([]);
  const [skillIsInitialLoading, setSkillIsInitialLoading] = useState(true);
  const [skillIsRefreshing, setSkillIsRefreshing] = useState(false);

  // SPELL variables
  const [spellsAdd, setSpellsAdd] = useState([]);
  const [spellSearch, setSpellSearch] = useState("");
  const [spellPageSize, setSpellPageSize] = useState(6);
  const [spellPage, setSpellPage] = useState(1);
  const [spellList, setSpellList] = useState([]);
  const [spellListInfos, setSpellListInfos] = useState([]);
  const [spellIsInitialLoading, setSpellIsInitialLoading] = useState(true);
  const [spellIsRefreshing, setSpellIsRefreshing] = useState(false);

  // JOB methods
  const handleJobPageChange = (newJobPage) => {
    setJobPage(newJobPage);
  };

  const handleJobChange = (selectedJob) => {
    const jobId = selectedJob.id;

    setJobsAdd((prevJobs) => {
      const updatedNewJobs = prevJobs.some((job) => job.id === jobId)
        ? prevJobs.filter((job) => job.id !== jobId)
        : [...prevJobs, selectedJob];

      // Trie les jobs par nom
      return updatedNewJobs.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  // SKILL methods
  const handleSkillPageChange = (newSkillPage) => {
    setSkillPage(newSkillPage);
  };

  const handleSkillChange = (selectedSkill) => {
    const skillId = selectedSkill.id;

    setSkillsAdd((prevSkills) => {
      const updatedNewSkills = prevSkills.some((skill) => skill.id === skillId)
        ? prevSkills.filter((skill) => skill.id !== skillId)
        : [...prevSkills, selectedSkill];

      // Trie les compétences par nom
      return updatedNewSkills.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  // SPELL methods
  const handleSpellPageChange = (newSpellPage) => {
    setSpellPage(newSpellPage);
  };

  const handleSpellChange = (selectedSpell) => {
    const spellId = selectedSpell.id;

    setSpellsAdd((prevSpells) => {
      const updatedNewSpells = prevSpells.some((spell) => spell.id === spellId)
        ? prevSpells.filter((spell) => spell.id !== spellId)
        : [...prevSpells, selectedSpell];

      // Trie les sorts par nom
      return updatedNewSpells.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  // JOB search
  useEffect(() => {
    const fetchJobTemplates = async () => {
      if (jobList.length === 0) setJobIsInitialLoading(true);
      else setJobIsRefreshing(true);

      try {
        const response = await api.get(
          `/jobtemplate/?search=${jobSearch}&is_natal=false&ordering=name&page_size=${jobPageSize}&page=${jobPage}`
        );
        setJobList(response.data.results);
        setJobListInfos(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setJobIsInitialLoading(false);
        setJobIsRefreshing(false);
      }
    };

    fetchJobTemplates();
  }, [jobSearch, jobPage, jobPageSize]);

  // SKILL search
  useEffect(() => {
    const fetchSkillTemplates = async () => {
      if (skillList.length === 0) setSkillIsInitialLoading(true);
      else setSkillIsRefreshing(true);

      try {
        const response = await api.get(
          `/skilltemplate/?search=${skillSearch}&ordering=name&page_size=${skillPageSize}&page=${skillPage}`
        );
        setSkillList(response.data.results);
        setSkillListInfos(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setSkillIsInitialLoading(false);
        setSkillIsRefreshing(false);
      }
    };

    fetchSkillTemplates();
  }, [skillSearch, skillPage, skillPageSize]);

  // SPELL search
  useEffect(() => {
    const fetchSpellTemplates = async () => {
      if (spellList.length === 0) setSpellIsInitialLoading(true);
      else setSpellIsRefreshing(true);

      try {
        const response = await api.get(
          `/spelltemplate/?search=${spellSearch}&ordering=name&page_size=${spellPageSize}&page=${spellPage}`
        );
        setSpellList(response.data.results);
        setSpellListInfos(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setSpellIsInitialLoading(false);
        setSpellIsRefreshing(false);
      }
    };

    fetchSpellTemplates();
  }, [spellSearch, spellPage, spellPageSize]);

  // Réinitialisation des valeurs
  const handleReset = () => {
    setJobsAdd([]);
    setSkillsAdd([]);
    setSpellsAdd([]);
  };

  // Vérification de la validité du personnage
  const checkUpdate = () => {
    setMessageError();

    // Vérification si au moins un skill ou un spell est amélioré
    if (
      Object.keys(jobsAdd).length === 0 &&
      Object.keys(skillsAdd).length === 0 &&
      Object.keys(spellsAdd).length === 0
    ) {
      setMessageError(
        "Veuillez ajouter au moins un métier, un skill ou un spell."
      );
      return false;
    }

    return true;
  };

  // Mise à jour du personnage
  const handleUpdateCharacter = () => {
    if (!checkUpdate()) {
      return;
    }
    setIsLoading(true);

    // Récupération des ids des jobs, skills et spells à ajouter
    const jobsId = jobsAdd.map((job) => job.id);
    const skillsId = skillsAdd.map((skill) => skill.id);
    const spellsId = spellsAdd.map((spell) => spell.id);

    api
      .post(`/addjobskillspell/`, {
        jobs: jobsId,
        skills: skillsId,
        spells: spellsId,
        characterId: character.id,
      })
      .then(() => {
        setRefresh(true);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });

    setIsLoading(false);
  };

  return (
    <>
      <div className="character-update">
        {isLoading ? (
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <div className="loading-text">Chargement de l'application...</div>
          </div>
        ) : (
          <>
            {/* Header du personnage */}
            <div className="header">
              <h1>Amélioration de : {character.name}</h1>
              <p>Propriétaire : {character.user_name}</p>
            </div>

            {/* Affichage des ajouts */}
            <section className="add-section">
              <h2>Métiers / Compétences / Sorts ajoutés</h2>
              {/* Métiers */}
              <div className="add-elements center">
                <h3>Métiers ({jobsAdd.length})</h3>
                {jobsAdd.length != 0 &&
                  jobsAdd.map((job, index) => (
                    <div className="add-element" key={index}>
                      <span>{job.name}</span>
                      <button
                        className="bt remove"
                        type="button"
                        onClick={() => handleJobChange(job)}
                      >
                        -
                      </button>
                    </div>
                  ))}
              </div>
              {/* Compétences */}
              <div className="add-elements center">
                <h3>Compétences ({skillsAdd.length})</h3>
                {skillsAdd.length != 0 &&
                  skillsAdd.map((skill, index) => (
                    <div className="add-element" key={index}>
                      <span>
                        {skill.name}{" "}
                        <small>
                          (
                          {skill.attributes
                            .map((attribute) => attribute.full_name)
                            .join(", ")}
                          )
                        </small>
                      </span>
                      <button
                        className="bt remove"
                        type="button"
                        onClick={() => handleSkillChange(skill)}
                      >
                        -
                      </button>
                    </div>
                  ))}
              </div>
              {/* Sorts */}
              <div className="add-elements center">
                <h3>Sorts ({spellsAdd.length})</h3>
                {spellsAdd.length != 0 &&
                  spellsAdd.map((spell, index) => (
                    <div className="add-element" key={index}>
                      <span>
                        {spell.name}{" "}
                        <small>
                          (
                          {spell.attributes
                            .map((attribute) => attribute.full_name)
                            .join(", ")}
                          )
                        </small>
                      </span>
                      <button
                        className="bt remove"
                        type="button"
                        onClick={() => handleSpellChange(spell)}
                      >
                        -
                      </button>
                    </div>
                  ))}
              </div>
            </section>

            {/* Affichage des élements possédés */}
            <section className="add-section">
              <h2>Métiers / Compétences / Sorts possédés</h2>
              {/* Affichage des métiers */}
              <div className="add-elements center">
                <h3>Métiers possédés ({jobsOwned.length})</h3>
                {jobsOwned.map((job) => (
                  <div className="add-element" key={job.id}>
                    <span>{job.name}</span>
                  </div>
                ))}
              </div>
              {/* Affichage des compétences */}
              <div className="add-elements center">
                <h3>Compétences possédées ({skillsOwned.length})</h3>
                {skillsOwned.map((skill) => (
                  <div key={skill.id} className="add-element">
                    <span>{skill.name}</span>
                  </div>
                ))}
              </div>
              {/* Affichage des sorts */}
              <div className="add-elements center">
                <h3>Sorts possédés ({spellsOwned.length})</h3>
                {spellsOwned.map((spell) => (
                  <div key={spell.id} className="add-element">
                    <span>{spell.name}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Ajout des métiers / compétences / sorts */}
            <section className="add-section">
              <h2>Ajouter Métiers / Compétences / Sorts</h2>
              {/* Ajout des métiers */}
              <div className="add-elements list-page">
                <h3>Liste des métiers ({jobListInfos.count})</h3>
                {/* Filtres */}
                <div className="filters">
                  <div className="job-search">
                    <label htmlFor="jobsearch">Rechercher </label>
                    <input
                      type="text"
                      name="jobsearch"
                      id="jobsearch"
                      value={jobSearch}
                      onChange={(e) => (
                        setJobSearch(e.target.value), setJobPage(1)
                      )}
                    />
                  </div>
                  <div className="job-pagesize">
                    <label htmlFor="jobpagesize">Nombres d'affichage </label>
                    <select
                      name="jobpagesize"
                      id="jobpagesize"
                      value={jobPageSize}
                      onChange={(e) => (
                        setJobPageSize(e.target.value), setJobPage(1)
                      )}
                    >
                      <option value="6">6</option>
                      <option value="12">12</option>
                      <option value="24">24</option>
                    </select>
                  </div>
                </div>

                {/* Chargement initial */}
                {jobIsInitialLoading && (
                  <div className="loading-data">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Chargement...</div>
                  </div>
                )}

                {/* Liste des métiers */}
                {!jobIsInitialLoading &&
                  (jobList.length === 0 ? (
                    <div className="no-data">
                      <p>
                        <strong>Aucun métier trouvé</strong>
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`list-div ${jobIsRefreshing ? "loading" : ""}`}
                    >
                      {jobList.map((job) => {
                        const isJobSelected = jobsAdd.some(
                          (jobSelected) => jobSelected.id === job.id
                        );
                        const isJobOwned = jobsOwned.some(
                          (jobOwned) => jobOwned.job_template === job.id
                        );
                        return (
                          <div className="list-card" key={job.id}>
                            {isJobOwned ? (
                              <div className="owned">
                                <strong>{job.name}</strong>{" "}
                                <small>(possédé)</small>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <strong>{job.name}</strong>
                                </div>
                                <button
                                  type="button"
                                  className={
                                    isJobSelected ? "bt remove" : "bt add"
                                  }
                                  onClick={() => handleJobChange(job)}
                                >
                                  {isJobSelected ? "-" : "+"}
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                {/* Pagination */}
                {!jobIsInitialLoading && jobList.length > 0 && (
                  <div className="pagination">
                    <button
                      className={"bt " + (jobPage === 1 ? "disabled" : "")}
                      type="button"
                      onClick={() => handleJobPageChange(jobPage - 1)}
                      disabled={jobPage === 1}
                    >
                      &lt;&lt;
                    </button>
                    <span>
                      {jobPage} / {Math.ceil(jobListInfos.count / jobPageSize)}
                    </span>
                    <button
                      className={
                        "bt " +
                        (jobPage >= Math.ceil(jobListInfos.count / jobPageSize)
                          ? "disabled"
                          : "")
                      }
                      type="button"
                      onClick={() => handleJobPageChange(jobPage + 1)}
                      disabled={
                        jobPage >= Math.ceil(jobListInfos.count / jobPageSize)
                      }
                    >
                      &gt;&gt;
                    </button>
                  </div>
                )}
              </div>

              {/* Ajout des compétences */}
              <div className="add-elements list-page">
                <h3>Liste des compétences ({skillListInfos.count})</h3>
                {/* Filtres */}
                <div className="filters">
                  <div className="skill-search">
                    <label htmlFor="skillsearch">Rechercher </label>
                    <input
                      type="text"
                      name="skillsearch"
                      id="skillsearch"
                      value={skillSearch}
                      onChange={(e) => (
                        setSkillSearch(e.target.value), setSkillPage(1)
                      )}
                    />
                  </div>
                  <div className="skill-pagesize">
                    <label htmlFor="skillpagesize">Nombres d'affichage </label>
                    <select
                      name="skillpagesize"
                      id="skillpagesize"
                      value={skillPageSize}
                      onChange={(e) => (
                        setSkillPageSize(e.target.value), setSkillPage(1)
                      )}
                    >
                      <option value="6">6</option>
                      <option value="12">12</option>
                      <option value="24">24</option>
                    </select>
                  </div>
                </div>

                {/* Chargement initial */}
                {skillIsInitialLoading && (
                  <div className="loading-data">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Chargement...</div>
                  </div>
                )}

                {/* Liste des compétences */}
                {!skillIsInitialLoading &&
                  (skillList.length === 0 ? (
                    <div className="no-data">
                      <p>
                        <strong>Aucune compétence trouvée</strong>
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`list-div skills ${
                        skillIsRefreshing ? "loading" : ""
                      }`}
                    >
                      {skillList.map((skill) => {
                        const isSkillSelected = skillsAdd.some(
                          (skillSelected) => skillSelected.id === skill.id
                        );
                        const isSkillOwned = skillsOwned.some(
                          (skillOwned) =>
                            skillOwned.skill_template_id === skill.id
                        );
                        return (
                          <div className="list-card" key={skill.id}>
                            {isSkillOwned ? (
                              <div className="owned">
                                <strong>{skill.name}</strong>{" "}
                                <small>
                                  (
                                  {skill.attributes
                                    .map((attribute) => attribute.full_name)
                                    .join(", ")}
                                  ) (possédé)
                                </small>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <strong>{skill.name}</strong>{" "}
                                  <small>
                                    (
                                    {skill.attributes
                                      .map((attribute) => attribute.full_name)
                                      .join(", ")}
                                    )
                                  </small>
                                </div>

                                <button
                                  type="button"
                                  className={
                                    isSkillSelected ? "bt remove" : "bt add"
                                  }
                                  onClick={() => handleSkillChange(skill)}
                                >
                                  {isSkillSelected ? "-" : "+"}
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                {/* Pagination */}
                {!skillIsInitialLoading && skillList.length > 0 && (
                  <div className="pagination">
                    <button
                      className={"bt " + (skillPage === 1 ? "disabled" : "")}
                      type="button"
                      onClick={() => handleSkillPageChange(skillPage - 1)}
                      disabled={skillPage === 1}
                    >
                      &lt;&lt;
                    </button>
                    <span>
                      {skillPage} /{" "}
                      {Math.ceil(skillListInfos.count / skillPageSize)}
                    </span>
                    <button
                      className={
                        "bt " +
                        (skillPage >=
                        Math.ceil(skillListInfos.count / skillPageSize)
                          ? "disabled"
                          : "")
                      }
                      type="button"
                      onClick={() => handleSkillPageChange(skillPage + 1)}
                      disabled={
                        skillPage >=
                        Math.ceil(skillListInfos.count / skillPageSize)
                      }
                    >
                      &gt;&gt;
                    </button>
                  </div>
                )}
              </div>

              {/* Ajout des sorts */}
              <div className="add-elements list-page">
                <h3>Liste des sorts ({spellListInfos.count})</h3>
                {/* Filters */}
                <div className="filters">
                  <div className="spell-search">
                    <label htmlFor="spellsearch">Rechercher un sort </label>
                    <input
                      type="text"
                      name="spellsearch"
                      id="spellsearch"
                      value={spellSearch}
                      onChange={(e) => (
                        setSpellSearch(e.target.value), setSpellPage(1)
                      )}
                    />
                  </div>
                  <div className="spell-pagesize">
                    <label htmlFor="spellpagesize">Taille de la page </label>
                    <select
                      name="spellpagesize"
                      id="spellpagesize"
                      value={spellPageSize}
                      onChange={(e) => (
                        setSpellPageSize(e.target.value), setSpellPage(1)
                      )}
                    >
                      <option value="6">6</option>
                      <option value="12">12</option>
                      <option value="24">24</option>
                    </select>
                  </div>
                </div>

                {/* Chargement initial */}
                {spellIsInitialLoading && (
                  <div className="loading-data">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Chargement...</div>
                  </div>
                )}

                {/* Liste des sorts */}
                {!spellIsInitialLoading &&
                  (spellList.length === 0 ? (
                    <div className="no-data">
                      <p>
                        <strong>Aucun sort trouvé</strong>
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`list-div spells ${
                        spellIsRefreshing ? "loading" : ""
                      }`}
                    >
                      {spellList.map((spell) => {
                        const isSpellSelected = spellsAdd.some(
                          (spellSelected) => spellSelected.id === spell.id
                        );
                        const isSpellOwned = spellsOwned.some(
                          (spellOwned) =>
                            spellOwned.spell_template_id === spell.id
                        );
                        return (
                          <div className="list-card" key={spell.id}>
                            {isSpellOwned ? (
                              <div className="owned">
                                <strong>{spell.name}</strong>{" "}
                                <small>
                                  (
                                  {spell.attributes
                                    .map((attribute) => attribute.full_name)
                                    .join(", ")}
                                  ) (possédé)
                                </small>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <strong>{spell.name}</strong>{" "}
                                  <small>
                                    (
                                    {spell.attributes
                                      .map((attribute) => attribute.full_name)
                                      .join(", ")}
                                    )
                                  </small>
                                </div>

                                <button
                                  type="button"
                                  className={
                                    isSpellSelected ? "bt remove" : "bt add"
                                  }
                                  onClick={() => handleSpellChange(spell)}
                                >
                                  {isSpellSelected ? "-" : "+"}
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                {/* Pagination */}
                {!spellIsInitialLoading && spellList.length > 0 && (
                  <div className="pagination">
                    <button
                      className={"bt " + (spellPage === 1 ? "disabled" : "")}
                      type="button"
                      onClick={() => handleSpellPageChange(spellPage - 1)}
                      disabled={spellPage === 1}
                    >
                      &lt;&lt;
                    </button>
                    <span>
                      {spellPage} /{" "}
                      {Math.ceil(spellListInfos.count / spellPageSize)}
                    </span>
                    <button
                      className={
                        "bt " +
                        (spellPage >=
                        Math.ceil(spellListInfos.count / spellPageSize)
                          ? "disabled"
                          : "")
                      }
                      type="button"
                      onClick={() => handleSpellPageChange(spellPage + 1)}
                      disabled={
                        spellPage >=
                        Math.ceil(spellListInfos.count / spellPageSize)
                      }
                    >
                      &gt;&gt;
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* Boutons de validation et annulation */}
            <div className="buttons">
              <button
                className={
                  jobsAdd.length === 0 &&
                  skillsAdd.length === 0 &&
                  spellsAdd.length === 0
                    ? "bt disabled"
                    : "bt"
                }
                onClick={handleUpdateCharacter}
                disabled={
                  jobsAdd.length === 0 &&
                  skillsAdd.length === 0 &&
                  spellsAdd.length === 0
                }
              >
                Valider
              </button>
              <button className="bt" onClick={() => setIsUpdate(false)}>
                Annuler
              </button>
              <button
                className={
                  jobsAdd.length === 0 &&
                  skillsAdd.length === 0 &&
                  spellsAdd.length === 0
                    ? "bt disabled"
                    : "bt"
                }
                onClick={handleReset}
                disabled={
                  jobsAdd.length === 0 &&
                  skillsAdd.length === 0 &&
                  spellsAdd.length === 0
                }
              >
                Reinitialiser
              </button>
            </div>
            {messageError && <p className="error">{messageError}</p>}
          </>
        )}
      </div>
    </>
  );
};

export default CharacterUpdate;
